/**
 * Local JSON file storage implementation
 * This implementation loads content from local JSON files
 * Perfect for development and as a fallback
 */

import { IContentStorage, ContentData, CommonContent, Category, StorageMetadata } from './contentStorage.interface';

export class LocalStorageService implements IContentStorage {
  private contentData: ContentData | null = null;
  private metadata: StorageMetadata = {
    version: 1,
    lastUpdated: new Date().toISOString()
  };

  async init(config?: Record<string, any>): Promise<void> {
    // For local storage, we can optionally load from a config
    // or just wait for the first data request
    console.log('[LocalStorage] Initialized');
  }

  async getCommon(): Promise<CommonContent> {
    await this.ensureDataLoaded();
    return this.contentData!.common;
  }

  async getCategory(id: number): Promise<Category | null> {
    await this.ensureDataLoaded();
    return this.contentData!.categories.find(cat => cat.id === id) || null;
  }

  async getAllCategories(): Promise<Category[]> {
    await this.ensureDataLoaded();
    return this.contentData!.categories;
  }

  async getAllContent(): Promise<ContentData> {
    await this.ensureDataLoaded();
    return this.contentData!;
  }

  async updateCommon(data: CommonContent): Promise<void> {
    await this.ensureDataLoaded();
    this.contentData!.common = data;
    this.updateMetadata();
    console.log('[LocalStorage] Common content updated');
  }

  async updateCategory(id: number, data: Category): Promise<void> {
    await this.ensureDataLoaded();
    const index = this.contentData!.categories.findIndex(cat => cat.id === id);
    if (index !== -1) {
      this.contentData!.categories[index] = data;
      this.updateMetadata();
      console.log(`[LocalStorage] Category ${id} updated`);
    } else {
      throw new Error(`Category with id ${id} not found`);
    }
  }

  async updateAllContent(data: ContentData): Promise<void> {
    this.contentData = data;
    this.updateMetadata();
    console.log('[LocalStorage] All content updated');
  }

  async getMetadata(): Promise<StorageMetadata> {
    return this.metadata;
  }

  /**
   * Load content data from the imported module
   * In production, this would load from actual JSON files
   */
  private async ensureDataLoaded(): Promise<void> {
    if (this.contentData) {
      return;
    }

    // Import the static data
    // We'll create this file next
    try {
      const module = await import('@/data/content.json');
      this.contentData = module.default || module;
      console.log('[LocalStorage] Content loaded from JSON');
    } catch (error) {
      console.error('[LocalStorage] Failed to load content:', error);
      throw new Error('Failed to load content data');
    }
  }

  private updateMetadata(): void {
    this.metadata.version += 1;
    this.metadata.lastUpdated = new Date().toISOString();
  }

  /**
   * Clear the cached content
   * Forces a fresh load on next access
   */
  clearCache(): void {
    this.contentData = null;
    console.log('[LocalStorage] Cache cleared');
  }

  /**
   * Helper method to export current content as JSON
   * Useful for migrating to external storage
   */
  async exportContent(): Promise<string> {
    await this.ensureDataLoaded();
    return JSON.stringify(
      {
        metadata: this.metadata,
        content: this.contentData
      },
      null,
      2
    );
  }
}
